import { z } from 'zod';
import { MoneySchema } from './common.js';
// VAT figures arrive as Decimal-strings from Prisma; accept either form on
// the wire (matches the convention used by ar.ts / journal-entry.ts).
const DecimalLike = z.union([MoneySchema, z.coerce.number()]);
// ============================================================
// VatFilingStatus enum — mirrors Prisma enum.
// ============================================================
export const VatFilingStatusSchema = z.enum(['DRAFT', 'FILED', 'AMENDED']);
// ============================================================
// VAT register row — one journal line on 1131 (Input) or 2131
// (Output) enriched with the matching base (sales/purchase) amount
// and party (customer/vendor) name when discoverable via refKey.
// ============================================================
export const VatLineTypeSchema = z.enum(['OUTPUT', 'INPUT']);
export const VatRegisterRowSchema = z.object({
    // Stable identifier — `${entryId}:${lineNo}` so the UI can key rows
    // without inventing one.
    id: z.string(),
    type: VatLineTypeSchema,
    voucherNo: z.string(),
    voucherDate: z.coerce.date(),
    // The invoice number from refKey (e.g. INV-2604-000298). Falls back
    // to voucherNo when the JV line has no refKey set.
    refKey: z.string().nullable(),
    description: z.string().nullable(),
    // Net base amount (the non-VAT side of the same voucher). 0 when
    // the entry has no clearly-paired base line.
    base: DecimalLike,
    vatAmount: DecimalLike,
    // Best-effort party lookup: AR-side joins by refKey -> ArInvoice.customer.
    // AP-side stays null until the AP module lands (Phase 3+).
    party: z.string().nullable(),
    taxId: z.string().nullable(),
});
export const VatRegisterResponseSchema = z.object({
    period: z.string().regex(/^\d{6}$/),
    output: z.array(VatRegisterRowSchema),
    input: z.array(VatRegisterRowSchema),
});
// ============================================================
// VAT summary — aggregate for a single period.
// ============================================================
export const VatSummaryResponseSchema = z.object({
    period: z.string().regex(/^\d{6}$/),
    outputVat: DecimalLike,
    inputVat: DecimalLike,
    // payable = outputVat - inputVat (can be negative when input > output,
    // i.e. the company is in a refund position).
    payable: DecimalLike,
    // Sums of net (non-VAT) bases for the period — feeds the (1)/(3) lines
    // on the ภ.พ.30 form.
    salesBase: DecimalLike,
    purchaseBase: DecimalLike,
    transactions: z.number().int().nonnegative(),
});
// ============================================================
// VAT filing — persisted monthly record (ภ.พ.30 submission tracker).
// ============================================================
export const VatFilingPeriodSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    period: z.string().regex(/^\d{6}$/),
    branchId: z.uuid().nullable().optional(),
    filingDate: z.coerce.date(),
    status: VatFilingStatusSchema,
    outputVat: DecimalLike,
    inputVat: DecimalLike,
    payable: DecimalLike,
    fileRef: z.string().nullable(),
    filedBy: z.string().nullable(),
    filedAt: z.coerce.date().nullable(),
    notes: z.string().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// Wire shape for POST — server computes outputVat / inputVat / payable
// from the register at the moment of creation, so the client only sends
// the period + filing date + optional metadata.
export const CreateVatFilingSchema = z.object({
    period: z.string().regex(/^\d{6}$/),
    // null/omitted = head-office consolidated; a branchId files that tax-branch.
    branchId: z.uuid().nullable().optional(),
    filingDate: z.iso.date(),
    fileRef: z.string().optional(),
    notes: z.string().optional(),
});
// PATCH — status flip (DRAFT -> FILED / AMENDED) plus optional metadata.
export const UpdateVatFilingSchema = z.object({
    status: VatFilingStatusSchema.optional(),
    fileRef: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
});
//# sourceMappingURL=vat.js.map